import { FC } from 'react';

import { Details, Text } from '@servicetitan/anvil2';

import { parseReportSections } from './parse-report-sections';
import * as Styles from './report-sections.module.scss';
import { Markdown } from '../entity-components/markdown/markdown';
import { MessageCard } from '../message-card/message-card';

interface ReportSectionsProps {
    markdown: string;
}

/*
 * Renders a markdown report as collapsible cards, one per top-level section. The first section is
 * expanded by default, the rest collapsed. Falls back to plain markdown when the report has no
 * headers to split on.
 */
export const ReportSections: FC<ReportSectionsProps> = ({ markdown }) => {
    const { intro, sections } = parseReportSections(markdown);

    if (sections.length === 0) {
        return <Markdown text={markdown} />;
    }

    /*
     * Keys must be unique even when the report repeats a header, so disambiguate
     * duplicate titles with an occurrence counter (content-derived, not positional).
     */
    const titleCounts = new Map<string, number>();
    const keyedSections = sections.map(section => {
        const count = titleCounts.get(section.title) ?? 0;
        titleCounts.set(section.title, count + 1);
        return { ...section, key: count === 0 ? section.title : `${section.title}-${count}` };
    });

    return (
        <div className={Styles.reportSections}>
            {intro && <Markdown text={intro} />}
            {keyedSections.map((section, index) => (
                <MessageCard key={section.key} className={Styles.sectionCard}>
                    <Details defaultExpanded={index === 0}>
                        <Details.Summary>
                            <Text>
                                <b>{section.title}</b>
                            </Text>
                        </Details.Summary>
                        <Details.Content>
                            <Markdown text={section.body} />
                        </Details.Content>
                    </Details>
                </MessageCard>
            ))}
        </div>
    );
};
